import * as React from "react";
import { cn } from "../../lib/utils";
import { Spinner } from "../ui/feedback";
import { CircleAlertIcon } from "../icons";

export interface PaneStatusProps {
  /** Shows a spinner in the icon slot. */
  loading?: boolean;
  /** An icon for the slot when not loading. Error tone supplies its own if omitted. */
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** A button or two, rendered under the text. */
  action?: React.ReactNode;
  tone?: "neutral" | "error";
  /** Fill the available height and centre vertically; otherwise sits at the top. */
  fill?: boolean;
  /** Less vertical padding, for a status inside a sheet or a strip rather than a whole pane. */
  compact?: boolean;
}

/**
 * The one way the pane says "nothing to show yet, and here is why".
 *
 * Signing in, connecting to the API, reading the document, an empty list, a failed
 * request: each used to be its own arrangement of a spinner, a heading and a box.
 * Now they are all this, so they line up and can be restyled once.
 */
const PaneStatus = ({
  loading = false,
  icon,
  title,
  description,
  action,
  tone = "neutral",
  fill = false,
  compact = false,
}: PaneStatusProps) => {
  const slot = loading ? (
    <Spinner size={22} />
  ) : (
    (icon ?? (tone === "error" ? <CircleAlertIcon className="size-7 text-error" /> : null))
  );

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 px-6 text-center",
        fill ? "min-h-0 flex-1 justify-center" : "justify-start",
        compact ? "py-3" : fill ? "py-6" : "py-10"
      )}
    >
      {slot && <div className="flex text-muted-foreground">{slot}</div>}
      {(title || description) && (
        <div className="flex max-w-[280px] flex-col items-center gap-1">
          {title && <div className="text-subtitle1 text-strong">{title}</div>}
          {description && <div className="text-body2 text-muted-foreground">{description}</div>}
        </div>
      )}
      {action && <div className="flex flex-row gap-2 pt-1">{action}</div>}
    </div>
  );
};

export default PaneStatus;
